import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { ProfileUserService } from '../../user/profile.user.service';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';
import {
    TdDailyHuddlePeriodRange,
    TdRawAgentDataDates,
} from '../td-daily-huddle/td-daily-huddle.reports.model';

import {
    TdCompliancePeriodType,
    TdComplianceProcessedResult,
    TdComplianceQuestion,
    TdComplianceQuestionResult,
    TdComplianceReportPopulationType,
    TdComplianceReportResultParams,
    TdComplianceReportSelectionParams,
    TdComplianceResult,
    TdComplianceResultRow,
    TdComplianceSort,
} from './td-compliance.reports.model';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

@Component({
    selector: '[tdComplianceReport]',
    templateUrl: './td-compliance.reports.component.html',
    host: {
        class: 'cmps-reports-td-compliance',
    },
})
export class TdComplianceReportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    pendingData: boolean;
    noData: boolean = false;
    reportId: number;
    selectedParams: TdComplianceReportSelectionParams = {
        date: null,
        populationType: TdComplianceReportPopulationType.Individual,
        populationID: null,
        defaultPopulationID: null,
        invalidUser: false,
        invalidDate: false,
        pendingData: false,
        noData: false,
    };

    params: TdComplianceReportResultParams;

    sub: Subscription;
    selectedPopulation: string;
    selectedPopulationType: string;
    peerGroups: IOption[];
    superGroups: IOption[];
    processedData: TdComplianceProcessedResult[] = [];
    reportPopulations: IOption[] = [
        {
            label: 'SuperGroup',
            value: TdComplianceReportPopulationType.SuperGroup,
        },
        {
            label: 'PeerGroup',
            value: TdComplianceReportPopulationType.PeerGroup,
        },
        {
            label: 'Individual',
            value: TdComplianceReportPopulationType.Individual,
        },
    ];

    sort: TdComplianceSort;
    dateRanges: TdDailyHuddlePeriodRange[] = [];
    callComplianceDates: TdRawAgentDataDates;

    constructor(
        private readonly _reportService: ReportsService,
        private readonly _profileUserService: ProfileUserService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.selectedParams.pendingData = true;
        forkJoin([
            this._reportService.retrieveCallComplianceDates().pipe(
                take(1),
                // eslint-disable-next-line no-return-assign
                tap(
                    (complianceDates) =>
                        (this.callComplianceDates = complianceDates),
                ),
            ),
            this._profileUserService.listPeerGroups().pipe(
                take(1),
                map((peerGroups) =>
                    peerGroups
                        .filter((p) => p.enabled)
                        .map((p) => ({
                            value: p.id.toString(),
                            label: p.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((peerGroups) => (this.peerGroups = peerGroups)),
            ),
            this._profileUserService.listSuperGroups().pipe(
                take(1),
                map((superGroups) =>
                    superGroups
                        .filter((s) => s.enabled)
                        .map((s) => ({
                            value: s.id.toString(),
                            label: s.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((superGroups) => (this.superGroups = superGroups)),
            ),
            this._reportService.retrieveDefaultHierarchyUser().pipe(
                take(1),
                tap((user) => {
                    this.selectedParams = {
                        ...this.selectedParams,
                        populationID: user,
                        defaultPopulationID: user,
                    };
                }),
            ),
        ])
            .pipe(
                tap((res) => {
                    console.log('taping...', res);
                }),
                finalize(() => {
                    this.selectedParams.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (!this.reportId) {
            this.noData = true;
            return;
        }
        this.pendingData = true;
        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    if (result.resultSet && result.resultSet.length > 0) {
                        this.params = this.getParams(result);
                        this.getSelectedPopulation();
                        this.dateRanges = this.getDateRanges(result);
                        const data = toCamel<TdComplianceResultRow>(
                            JSON.parse(result.resultSet),
                        );
                        let processedData =
                            Array.isArray(data) && this.processData(data);
                        processedData = this.calculateSummary(processedData);
                        const sort = this.initializeSort(processedData);
                        this.sort = sort;
                        this.processedData = processedData;
                        this.noData = false;
                    }
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    private initializeSort(
        processedData: TdComplianceProcessedResult[],
    ): TdComplianceSort {
        const sort: TdComplianceSort = {};
        processedData.forEach((element) => {
            element.questions.forEach((question) => {
                question.results.forEach((result, index) => {
                    for (const key in result) {
                        // eslint-disable-next-line no-prototype-builtins
                        if (result.hasOwnProperty(key)) {
                            sort[`${key}-${index}`] = {
                                hasSort: false,
                                isAsc: false,
                            };
                        }
                    }
                });
            });
        });
        return sort;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private processData(data: TdComplianceResultRow[]) {
        const processData = data.reduce<TdComplianceProcessedResult[]>(
            (out, row) => {
                const questionResultRow: TdComplianceQuestionResult = {
                    periodType: row.periodType,
                    scored: row.scored,
                    passed: row.passed,
                    passRate: row.passRate,
                };
                const questionRow: TdComplianceQuestion = {
                    objective: row.objective,
                    question: row.question,
                    results: [questionResultRow],
                };
                const value = out.find((o) => o.category === row.category);
                if (value) {
                    const question = value.questions.find(
                        (q) =>
                            q.objective === row.objective &&
                            q.question === row.question,
                    );
                    if (question) {
                        question.results.push(questionResultRow);
                    } else {
                        value.questions.push(questionRow);
                    }
                } else {
                    const processedResultRow: TdComplianceProcessedResult = {
                        category: row.category,
                        summary: [],
                        questions: [questionRow],
                    };
                    out.push(processedResultRow);
                }
                return out;
            },
            [],
        );
        const periods: TdCompliancePeriodType[] = ['MTD', 'QTD', 'YTD'];
        processData.forEach((row) => {
            row.questions.forEach((question) => {
                periods.forEach((period, periodIndex) => {
                    if (
                        !question.results.some((r) => r.periodType === period)
                    ) {
                        question.results.splice(periodIndex, 0, {
                            periodType: period,
                            scored: '-',
                            passed: '-',
                            passRate: '-',
                        });
                    }
                });
            });
        });
        return processData;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private calculateSummary(processedData: TdComplianceProcessedResult[]) {
        return processedData.map((categoryRow) => {
            const summary = categoryRow.questions.reduce<
                TdComplianceQuestionResult[]
            >((out, row) => {
                if (out.length === 0) {
                    return row.results;
                }
                return out.map((ro) => {
                    const rr = row.results.find(
                        (r) => r.periodType === ro.periodType,
                    );
                    if (
                        rr &&
                        typeof rr.passRate === 'number' &&
                        typeof rr.passed === 'number' &&
                        typeof rr.scored === 'number'
                    ) {
                        return {
                            periodType: ro.periodType,
                            passRate: (ro.passRate as number) + rr.passRate,
                            passed: (ro.passed as number) + rr.passed,
                            scored: (ro.scored as number) + rr.scored,
                        };
                    } else {
                        return ro;
                    }
                });
            }, []);
            categoryRow.summary = summary;
            return categoryRow;
        });
    }

    private getDateRanges(
        result: TdComplianceResult,
    ): TdDailyHuddlePeriodRange[] {
        return (
            toCamel(JSON.parse(result.periods)) as TdDailyHuddlePeriodRange[]
        ).map((periodRange) => {
            const startDate = new Date(periodRange.startDate);
            const endDate = new Date(periodRange.endDate);
            return {
                periodName: periodRange.periodName,
                startDate: new Date(
                    startDate.getTime() + startDate.getTimezoneOffset() * 60000,
                ),
                endDate: new Date(
                    endDate.getTime() + endDate.getTimezoneOffset() * 60000,
                ),
            };
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getSelectedPopulation() {
        const selected = this.reportPopulations.find(
            (p) => p.label === this.params.populationType,
        );
        this.selectedPopulationType = selected.label;

        switch (selected.value) {
            case TdComplianceReportPopulationType.SuperGroup:
                // eslint-disable-next-line no-case-declarations
                const superGroup = this.superGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = superGroup ? superGroup.label : '';
                break;
            case TdComplianceReportPopulationType.PeerGroup:
                // eslint-disable-next-line no-case-declarations
                const peerGroup = this.superGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = peerGroup ? peerGroup.label : '';
                break;
            case TdComplianceReportPopulationType.Individual:
                this._reportService
                    .retrieveUsersNameById(this.params.populationID)
                    .pipe(
                        take(1),
                        tap((r) => {
                            if (r.length > 0) {
                                this.selectedPopulation = r[0].userName;
                            }
                        }),
                    )
                    .subscribe();
                break;
            default:
                break;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onClickSort(key: string) {
        for (const fieldName in this.sort) {
            // eslint-disable-next-line no-prototype-builtins
            if (this.sort.hasOwnProperty(fieldName)) {
                const element = this.sort[fieldName];
                if (key === fieldName) {
                    // eslint-disable-next-line no-unneeded-ternary
                    element.isAsc =
                        // eslint-disable-next-line no-unneeded-ternary
                        element.hasSort && element.isAsc ? false : true;
                    element.hasSort = true;
                    const field = key.split('-')[0];
                    const pos = parseInt(key.split('-')[1]);
                    this.processedData.forEach((result) => {
                        result.questions.sort((questionA, questionB) => {
                            if (element.isAsc) {
                                return questionA.results[pos][field] <
                                    questionB.results[pos][field]
                                    ? -1
                                    : 1;
                            }
                            return questionA.results[pos][field] <
                                questionB.results[pos][field]
                                ? 1
                                : -1;
                        });
                    });
                } else {
                    element.isAsc = false;
                    element.hasSort = false;
                }
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.tabSet.select('w-tab-report-1');
    }

    private getParams(
        result: TdComplianceResult,
    ): TdComplianceReportResultParams {
        return result.params.split('@').reduce((output, current) => {
            const keyValue = current.split('=').map((v) => v.trim());
            const key = (
                keyValue[0].charAt(0).toLowerCase() + keyValue[0].slice(1) ||
                keyValue[0]
            ).toString();
            const value = keyValue[1];
            if (key) {
                output[key] = value.replace(/^,+|,+$/g, '');
            }
            return output;
        }, {}) as TdComplianceReportResultParams;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report Compliance.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }
}
